import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Scale, ExternalLink } from "lucide-react";
import { DEEPSEEK_PRICING, getRate, type ModelKey } from "@/lib/deepseek-pricing";
import {
  COMPETITOR_PRICING,
  COMPETITOR_ORDER,
  type CompetitorKey,
  usd,
} from "@/lib/competitor-pricing";

/**
 * Cross-provider cost comparator.
 *
 * The point of this tool is verifiability, not marketing: every rate comes from
 * the two pricing libraries (DeepSeek + competitors), each row links to the
 * provider's own rate card, and the verification dates are printed next to the
 * result. No estimated discounts, no unannounced surcharges.
 */

const PRESETS = [
  {
    id: "chat",
    label: "Chat assistant",
    hint: "Short prompts, short answers, 50k calls/month",
    inputTokens: 900,
    outputTokens: 350,
    requests: 50_000,
    cacheHit: 40,
  },
  {
    id: "rag",
    label: "RAG / document Q&A",
    hint: "Long retrieved context, short answers",
    inputTokens: 12_000,
    outputTokens: 600,
    requests: 20_000,
    cacheHit: 60,
  },
  {
    id: "coding",
    label: "Coding agent",
    hint: "Large files in, long diffs out",
    inputTokens: 20_000,
    outputTokens: 4_000,
    requests: 6_000,
    cacheHit: 50,
  },
  {
    id: "batch",
    label: "Bulk classification",
    hint: "Millions of tiny calls",
    inputTokens: 400,
    outputTokens: 60,
    requests: 500_000,
    cacheHit: 20,
  },
] as const;

const DEEPSEEK_MODELS: ModelKey[] = ["v4-flash", "v4-pro"];

const money = (n: number) =>
  n < 1
    ? `$${n.toFixed(3)}`
    : `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

const compact = (n: number) =>
  n >= 1000 ? `${(n / 1000).toLocaleString("en-US", { maximumFractionDigits: 0 })}k` : String(n);

export default function CostComparator() {
  const [preset, setPreset] = useState<string>("chat");
  const [inputTokens, setInputTokens] = useState<number>(PRESETS[0].inputTokens);
  const [outputTokens, setOutputTokens] = useState<number>(PRESETS[0].outputTokens);
  const [requests, setRequests] = useState<number>(PRESETS[0].requests);
  const [cacheHit, setCacheHit] = useState<number>(PRESETS[0].cacheHit);

  const applyPreset = (id: string) => {
    const p = PRESETS.find((x) => x.id === id);
    if (!p) return;
    setPreset(id);
    setInputTokens(p.inputTokens);
    setOutputTokens(p.outputTokens);
    setRequests(p.requests);
    setCacheHit(p.cacheHit);
  };

  const rows = useMemo(() => {
    const per = DEEPSEEK_PRICING.perTokens;
    const ratio = cacheHit / 100;
    const inM = (inputTokens * requests) / per;
    const outM = (outputTokens * requests) / per;

    const deepseek = DEEPSEEK_MODELS.map((key) => {
      const r = getRate(key);
      const blendedInput = ratio * r.inputCacheHit + (1 - ratio) * r.inputCacheMiss;
      return {
        key,
        label: DEEPSEEK_PRICING.models[key].label,
        vendor: "DeepSeek",
        inputRate: r.inputCacheMiss,
        outputRate: r.output,
        monthly: inM * blendedInput + outM * r.output,
        source: DEEPSEEK_PRICING.source,
        isDeepSeek: true,
      };
    });

    const competitors = COMPETITOR_ORDER.map((key: CompetitorKey) => {
      const m = COMPETITOR_PRICING.models[key];
      const cached = "cachedInput" in m && m.cachedInput != null ? m.cachedInput : m.input;
      const blendedInput = ratio * cached + (1 - ratio) * m.input;
      return {
        key,
        label: m.label,
        vendor: m.vendor,
        inputRate: m.input,
        outputRate: m.output,
        monthly: inM * blendedInput + outM * m.output,
        source: m.source,
        isDeepSeek: false,
      };
    });

    return [...deepseek, ...competitors].sort((a, b) => a.monthly - b.monthly);
  }, [inputTokens, outputTokens, requests, cacheHit]);

  const cheapest = rows[0]?.monthly ?? 0;

  return (
    <Card className="border-2 border-primary/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-2xl">
          <Scale className="h-6 w-6 text-primary" />
          DeepSeek vs GPT, Claude and Gemini: monthly cost comparator
        </CardTitle>
        <CardDescription>
          Pick a workload, then see what the same volume costs on each provider's published API rates.
          DeepSeek rates verified {DEEPSEEK_PRICING.lastVerified}; competitor rates verified{" "}
          {COMPETITOR_PRICING.lastVerified}. Every row links to the provider's own price page.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p.id}
              type="button"
              variant={preset === p.id ? "default" : "outline"}
              size="sm"
              onClick={() => applyPreset(p.id)}
              title={p.hint}
            >
              {p.label}
            </Button>
          ))}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="cc-input">Input tokens per call: {compact(inputTokens)}</Label>
            <Slider
              id="cc-input"
              aria-label="Input tokens per call"
              min={100}
              max={64_000}
              step={100}
              value={[inputTokens]}
              onValueChange={([v]) => setInputTokens(v)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cc-output">Output tokens per call: {compact(outputTokens)}</Label>
            <Slider
              id="cc-output"
              aria-label="Output tokens per call"
              min={50}
              max={16_000}
              step={50}
              value={[outputTokens]}
              onValueChange={([v]) => setOutputTokens(v)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cc-requests">Calls per month: {requests.toLocaleString("en-US")}</Label>
            <Slider
              id="cc-requests"
              aria-label="Calls per month"
              min={1_000}
              max={1_000_000}
              step={1_000}
              value={[requests]}
              onValueChange={([v]) => setRequests(v)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cc-cache">Prompt cache hit rate: {cacheHit}%</Label>
            <Slider
              id="cc-cache"
              aria-label="Prompt cache hit rate"
              min={0}
              max={90}
              step={5}
              value={[cacheHit]}
              onValueChange={([v]) => setCacheHit(v)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">
              Estimated monthly API cost for the selected workload, per model
            </caption>
            <thead>
              <tr className="border-b text-left">
                <th scope="col" className="py-2 pr-4 font-semibold">Model</th>
                <th scope="col" className="py-2 pr-4 font-semibold">Input / 1M</th>
                <th scope="col" className="py-2 pr-4 font-semibold">Output / 1M</th>
                <th scope="col" className="py-2 pr-4 font-semibold">Monthly cost</th>
                <th scope="col" className="py-2 pr-4 font-semibold">vs cheapest</th>
                <th scope="col" className="py-2 font-semibold">Rate card</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.key}
                  className={`border-b last:border-0 ${r.isDeepSeek ? "bg-primary/5 font-medium" : ""}`}
                >
                  <td className="py-2 pr-4">
                    {r.label}
                    <span className="ml-2 text-xs text-muted-foreground">{r.vendor}</span>
                  </td>
                  <td className="py-2 pr-4">{usd(r.inputRate)}</td>
                  <td className="py-2 pr-4">{usd(r.outputRate)}</td>
                  <td className="py-2 pr-4">{money(r.monthly)}</td>
                  <td className="py-2 pr-4">
                    {cheapest > 0 && r.monthly > cheapest
                      ? `${(r.monthly / cheapest).toFixed(1)}× more`
                      : "cheapest"}
                  </td>
                  <td className="py-2">
                    <a
                      href={r.source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary inline-flex items-center gap-1 hover:underline"
                    >
                      source <ExternalLink className="h-3 w-3" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-muted-foreground">
          Method: monthly cost = (input tokens × calls × blended input rate) + (output tokens × calls ×
          output rate). The blended input rate mixes each provider's cached and uncached input price at
          your cache hit rate; providers without a published cached-input rate are billed at full input
          price. Batch discounts, long-context surcharges above a provider's threshold, free tiers and
          enterprise deals are not included.
          {DEEPSEEK_PRICING.peakSurcharge.announced && !DEEPSEEK_PRICING.peakSurcharge.applies
            ? " DeepSeek has announced a peak-hour surcharge but has not confirmed the percentage or start date, so it is not applied here."
            : DEEPSEEK_PRICING.peakSurcharge.applies
            ? ` DeepSeek rates shown are off-peak; peak hours (${DEEPSEEK_PRICING.peakSurcharge.windowUTC} UTC) are billed at double.`
            : ""}
        </p>
      </CardContent>
    </Card>
  );
}
